/**
 * A real document under the cursor (ui/DESIGN.md §3.2): pointing at a moving document on the map shows one
 * run of that integration, the document it read and the one it made of it side by side, the values carried
 * from one to the other marked in both. Ported from components/data/FlowSample.tsx, re-skinned with the
 * primitives: two quiet panes, format as a glyph and a word, never a coloured fill.
 */
import { Fragment } from 'react';
import dayjs from 'dayjs';
import { Api, type Hit, type Run } from '../lib/api.ts';
import { prettyPayload } from '../lib/format.ts';
import { stripSystem, systemName } from '../lib/systems.ts';
import { Glyph, NounIcon, Surface, SystemBadge, cx, noun } from '../design/primitives.tsx';
import { StatusWord, type MessageRef } from './lenses/common.tsx';

export interface SampleDoc { hit: Hit; system: string; lines: string[]; values: Set<string> }
/** A real example of what an integration moves: a document it read and the one it made of it, from one run. */
export interface FlowSample { run: Run; from: SampleDoc | null; to: SampleDoc | null; shared: string[] }
/** What the loader needs of a flow (a MapFlow is one). */
export interface SampleFlow { process: string; from: string; to: string | null; external?: string | null }

const SNIPPET = 12;
/** Values worth noticing again on the other side: not one-letter codes, booleans or tiny numbers. */
const notable = (v: string) => v.length >= 3 && !/^(true|false|null)$/i.test(v) && !/^\d{1,2}(\.\d+)?$/.test(v);

/** A run of the integration that touched both data models; its documents of each, matched by correlation where it can. */
export async function loadSample(tid: string, runs: Run[], f: SampleFlow, systemOf: (cls: string) => string): Promise<FlowSample | null> {
  const candidates = runs.filter((r) => r.process === f.process && r.classes.includes(f.from) && (!f.to || r.classes.includes(f.to)));
  const run = candidates.find((r) => r.failed === 0) ?? candidates[0] ?? runs.find((r) => r.process === f.process);
  if (!run) return null;
  const ex = await Api.execution(tid, run.executionId);
  const steps = ex.steps.filter((s) => s.items.length > 0);
  const fromStep = steps.find((s) => s.messageClass === f.from) ?? steps[0];
  const toStep = f.to ? steps.find((s) => s.messageClass === f.to && (!fromStep?.correlationId || s.correlationId === fromStep.correlationId))
    ?? steps.find((s) => s.messageClass === f.to) : undefined;
  const doc = async (h: Hit | undefined): Promise<SampleDoc | null> => {
    if (!h) return null;
    const [m, text] = await Promise.all([
      Api.message(tid, h.vaultId, h.uuid, h.day),
      fetch(Api.payloadUrl(tid, h.vaultId, h.uuid, h.day), { credentials: 'same-origin' }).then((r) => (r.ok ? r.text() : '')).catch(() => ''),
    ]);
    return {
      hit: h, system: systemOf(h.messageClass), lines: prettyPayload(text, h.type).split('\n'),
      values: new Set(m.fields.map(([, v]) => v.trim()).filter(notable)),
    };
  };
  const [from, to] = await Promise.all([doc(fromStep?.items[0]), doc(toStep?.items[0])]);
  const shared = from && to ? [...from.values].filter((v) => to.values.has(v)).sort((a, b) => b.length - a.length).slice(0, 40) : [];
  return { run, from, to, shared };
}

/** The card: the integration, a real run of it, the document in and the document out with the carried values marked. */
export function SampleCard({ flow, sample, error, onMessage }: {
  flow: SampleFlow; sample: FlowSample | null | undefined; error?: string; onMessage: (m: MessageRef) => void;
}) {
  return (
    <Surface level={2} radius="card" className="stage-sample w-[min(680px,calc(100vw-48px))] p-4 flex flex-col gap-3 mv-pop text-left">
      <div className="flex items-center gap-2.5 min-w-0">
        <NounIcon kind="run" size={18} />
        <span className="text-body font-medium truncate">{stripSystem(flow.process)}</span>
        {sample && <span className="font-mono text-small text-n7 whitespace-nowrap">{dayjs(sample.run.first).format('D.M. HH:mm:ss')}</span>}
        {sample && <StatusWord status={sample.run.failed ? 'FAILED' : 'SUCCESS'} />}
        {sample === undefined && !error && <span className="text-small text-n7">a real run of it</span>}
      </div>
      {error && <span className="text-small text-fail">{error}</span>}
      {sample === undefined && !error && (
        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
          <span className="h-44 rounded-card mv-shimmer" /><span className="text-n6" aria-hidden="true">→</span><span className="h-44 rounded-card mv-shimmer" style={{ animationDelay: '120ms' }} />
        </div>
      )}
      {sample === null && <span className="text-small text-n8">No run of it was found with both of its documents.</span>}
      {sample && (
        <>
          <div className={cx('grid gap-3 items-stretch', sample.to ? 'grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]' : 'grid-cols-1')}>
            {sample.from && <Pane d={sample.from} shared={sample.shared} onMessage={onMessage} />}
            {sample.to && (
              <>
                <span className="self-center text-title leading-none text-run" aria-hidden="true">→</span>
                <Pane d={sample.to} shared={sample.shared} onMessage={onMessage} />
              </>
            )}
            {!sample.to && flow.external && <span className="text-small text-n8">… and sends it on to {flow.external}.</span>}
          </div>
          {sample.from && sample.to && (
            <span className="text-small text-n7">
              {sample.shared.length
                ? <><mark>{sample.shared.length} {sample.shared.length === 1 ? 'value' : 'values'}</mark> carried from one document to the other</>
                : 'No value is carried as is: the run maps them to new names and forms.'}
            </span>
          )}
        </>
      )}
    </Surface>
  );
}

function Pane({ d, shared, onMessage }: { d: SampleDoc; shared: string[]; onMessage: (m: MessageRef) => void }) {
  // Start the snippet near the first carried value, so what moved is in view
  const first = Math.max(0, d.lines.findIndex((l) => shared.some((v) => l.includes(v))));
  const start = first > SNIPPET - 4 ? first - 3 : 0;
  const lines = d.lines.slice(start, start + SNIPPET);
  const format = (d.hit.type ?? '').toLowerCase();
  return (
    <div className="min-w-0 rounded-card bg-n1 flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-2 min-w-0">
        <SystemBadge code={d.system} size={20} />
        <span className="text-small font-medium truncate">{systemName(d.system)} {noun(d.hit.messageClass)}</span>
        <span className="ml-auto inline-flex items-center gap-1 font-mono text-micro text-n7 uppercase tracking-[.06em]"><Glyph format={format} size={14} />{format || 'text'}</span>
      </div>
      <pre className="px-3 pb-2">
        {start > 0 && <span className="text-n6">…{'\n'}</span>}
        {lines.map((l, i) => <Fragment key={i}><Marked line={l} shared={shared} />{'\n'}</Fragment>)}
        {start + SNIPPET < d.lines.length && <span className="text-n6">… {d.lines.length - start - SNIPPET} more lines</span>}
      </pre>
      <button type="button" onClick={() => onMessage({ vaultId: d.hit.vaultId, uuid: d.hit.uuid, day: d.hit.day })}
        className="mt-auto m-3 mt-0 self-start inline-flex items-center gap-1.5 h-7 pl-1.5 pr-2.5 rounded-full bg-doc-soft text-doc text-small font-medium border-0 cursor-pointer hover:brightness-95">
        <NounIcon kind="document" size={14} />open document
      </button>
    </div>
  );
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A line with the carried values marked. */
function Marked({ line, shared }: { line: string; shared: string[] }) {
  const hits = shared.filter((v) => line.includes(v));
  if (!hits.length) return <>{line}</>;
  const parts = line.split(new RegExp(`(${hits.map(escape).join('|')})`));
  return <>{parts.map((p, i) => (hits.includes(p) ? <mark key={i}>{p}</mark> : p))}</>;
}
